import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Sparkles, Send, Copy, Check, Save } from 'lucide-react';

/**
 * AnimatedMessageDemo - Matches the actual GenerateMessage.js UI and workflow
 * Demonstrates: Contact selection → Message type → Template → AI generation → Refinement
 */
const AnimatedMessageDemo = ({ autoPlay = true, loop = true }) => {
  const [step, setStep] = useState(0);
  const [isPlaying, setIsPlaying] = useState(autoPlay);
  const [displayedMessage, setDisplayedMessage] = useState('');
  const [displayedRefinement, setDisplayedRefinement] = useState('');
  const [conversationMessages, setConversationMessages] = useState([]);

  // Demo data matching real workflow
  const demoContact = {
    name: 'Sarah Chen',
    company: 'TechVentures Inc.',
    role: 'VP of Product',
    connection: 'Met at Tech Conference 2025',
    interests: 'AI, Product Development, SaaS'
  };

  const messageType = 'follow_up';
  const medium = 'email';
  const selectedTemplate = 'warm_personal';

  const initialMessage = `Hi Sarah,

Hope you're doing well! I've been thinking about our conversation at the Tech Conference last month about AI integration in product development.

I came across an interesting article on context-aware AI systems that reminded me of what you mentioned about TechVentures' new platform. Would love to share it with you and hear your thoughts.

Would you be open to a quick coffee chat next week to catch up?

Looking forward to connecting!

Best,
[Your name]`;

  const userRefinement = "Make it more casual and mention the conference session we both attended";

  const refinedMessage = `Hey Sarah!

Hope all is well! Been thinking about that awesome panel discussion we both attended at the Tech Conference - your insights on AI integration were spot on.

Actually came across an article that ties directly into what you were saying about context-aware systems. Thought you'd find it interesting!

Want to grab coffee next week and chat more about it?

Cheers,
[Your name]`;

  // Animation steps matching real workflow
  const steps = [
    { name: 'selectContact', duration: 2500 },
    { name: 'selectOptions', duration: 2000 },
    { name: 'selectTemplate', duration: 2000 },
    { name: 'generating', duration: 2500 },
    { name: 'showMessage', duration: 9500 },
    { name: 'userInput', duration: 2500 },
    { name: 'refining', duration: 2000 },
    { name: 'showRefined', duration: 6000 },
    { name: 'complete', duration: 2000 }
  ];

  // Typing effect
  const typeMessage = (text, setter, speed = 20) => {
    let i = 0;
    setter('');
    const interval = setInterval(() => {
      if (i < text.length) {
        setter(text.substring(0, i + 1));
        i++;
      } else {
        clearInterval(interval);
      }
    }, speed);
    return interval;
  };

  useEffect(() => {
    if (!isPlaying) return;

    let timeout;
    let typingInterval;

    if (step < steps.length) {
      const currentStep = steps[step];

      // Handle typing for messages
      if (currentStep.name === 'showMessage') {
        typingInterval = typeMessage(initialMessage, setDisplayedMessage);
      } else if (currentStep.name === 'userInput') {
        typingInterval = typeMessage(userRefinement, setDisplayedRefinement);
      } else if (currentStep.name === 'showRefined') {
        setDisplayedMessage(refinedMessage);
      }

      timeout = setTimeout(() => {
        setStep(step + 1);
      }, currentStep.duration);
    } else if (loop) {
      timeout = setTimeout(() => {
        setStep(0);
        setDisplayedMessage('');
        setDisplayedRefinement('');
        setConversationMessages([]);
      }, 2000);
    }

    return () => {
      clearTimeout(timeout);
      if (typingInterval) clearInterval(typingInterval);
    };
  }, [step, isPlaying, loop]);

  const currentStepName = steps[step]?.name || 'complete';

  // Styles matching GenerateMessage.js
  const styles = {
    container: {
      width: '100%',
      maxWidth: '1000px',
      margin: '0 auto',
      padding: '2rem',
      background: '#ffffff',
      borderRadius: '16px',
      boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)',
      border: '1px solid #e2e8f0',
    },
    card: {
      background: '#ffffff',
      borderRadius: '16px',
      padding: '2rem',
      boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)',
      border: '1px solid #e2e8f0',
      marginBottom: '1.5rem',
    },
    formGroup: {
      marginBottom: '1.5rem',
    },
    label: {
      display: 'block',
      marginBottom: '0.5rem',
      color: '#0f172a',
      fontWeight: 500,
      fontSize: '0.95rem',
    },
    select: {
      width: '100%',
      padding: '0.875rem 1rem',
      border: '2px solid #e2e8f0',
      borderRadius: '12px',
      fontSize: '1rem',
      background: '#f8fafc',
      outline: 'none',
      color: '#0f172a',
    },
    contactInfo: {
      background: '#eff6ff',
      border: '1px solid #bfdbfe',
      borderRadius: '8px',
      padding: '1rem',
      marginTop: '1rem',
    },
    contactInfoTitle: {
      fontWeight: 600,
      fontSize: '0.95rem',
      color: '#1e40af',
      marginBottom: '0.5rem',
    },
    contactInfoText: {
      fontSize: '0.85rem',
      color: '#475569',
      lineHeight: 1.5,
    },
    button: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: '0.5rem',
      padding: '0.875rem 1.5rem',
      border: 'none',
      borderRadius: '12px',
      fontSize: '1rem',
      fontWeight: 600,
      cursor: 'pointer',
      fontFamily: "'Syne', sans-serif",
      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      color: 'white',
      boxShadow: '0 4px 12px rgba(102, 126, 234, 0.4)',
      width: '100%',
    },
    loadingContainer: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '3rem 1rem',
    },
    loadingDots: {
      display: 'flex',
      gap: '0.5rem',
      alignItems: 'center',
    },
    loadingDot: {
      width: '12px',
      height: '12px',
      borderRadius: '50%',
      background: '#667eea',
    },
    conversationContainer: {
      maxHeight: '500px',
      overflowY: 'auto',
      marginBottom: '1rem',
      padding: '0.5rem',
    },
    messageItem: {
      marginBottom: '1rem',
      padding: '1rem',
      borderRadius: '12px',
      position: 'relative',
    },
    userMessage: {
      background: '#eff6ff',
      border: '1px solid #bfdbfe',
      marginLeft: '2rem',
    },
    aiMessage: {
      background: '#f8fafc',
      border: '1px solid #e2e8f0',
      marginRight: '2rem',
    },
    messageLabel: {
      fontSize: '0.75rem',
      fontWeight: 600,
      color: '#64748b',
      marginBottom: '0.5rem',
      textTransform: 'uppercase',
      letterSpacing: '0.5px',
    },
    messageText: {
      fontSize: '0.95rem',
      lineHeight: 1.6,
      color: '#0f172a',
      whiteSpace: 'pre-wrap',
    },
    refinementInput: {
      display: 'flex',
      gap: '0.75rem',
      alignItems: 'flex-end',
    },
    refinementTextarea: {
      flex: 1,
      padding: '0.875rem 1rem',
      border: '2px solid #e2e8f0',
      borderRadius: '12px',
      fontSize: '0.95rem',
      fontFamily: 'inherit',
      background: '#ffffff',
      outline: 'none',
      minHeight: '80px',
      resize: 'vertical',
      color: '#0f172a',
    },
    sendButton: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: '0.5rem',
      padding: '0.875rem 1.25rem',
      border: 'none',
      borderRadius: '12px',
      fontSize: '0.95rem',
      fontWeight: 600,
      cursor: 'pointer',
      fontFamily: "'Syne', sans-serif",
      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      color: 'white',
      boxShadow: '0 4px 12px rgba(102, 126, 234, 0.4)',
      whiteSpace: 'nowrap',
      height: 'fit-content',
    },
    actionButtonsContainer: {
      position: 'absolute',
      top: '0.75rem',
      right: '0.75rem',
      display: 'flex',
      gap: '0.5rem',
    },
    actionButton: {
      display: 'flex',
      alignItems: 'center',
      gap: '0.35rem',
      padding: '0.4rem 0.75rem',
      border: 'none',
      borderRadius: '6px',
      fontSize: '0.75rem',
      fontWeight: 600,
      cursor: 'pointer',
      fontFamily: "'Syne', sans-serif",
      boxShadow: '0 2px 6px rgba(0, 0, 0, 0.1)',
    },
    copyButton: {
      background: '#2563eb',
      color: 'white',
    },
    saveButton: {
      background: '#8b5cf6',
      color: 'white',
    },
    templateCard: {
      padding: '1rem',
      border: '2px solid #667eea',
      borderRadius: '12px',
      background: '#eff6ff',
      boxShadow: '0 0 0 3px rgba(102, 126, 234, 0.1)',
      marginBottom: '0.75rem',
    },
    templateLabel: {
      fontWeight: 600,
      fontSize: '0.95rem',
      color: '#0f172a',
      marginBottom: '0.25rem',
    },
    templateDescription: {
      fontSize: '0.85rem',
      color: '#64748b',
    },
  };

  const LoadingDots = () => (
    <div style={styles.loadingContainer}>
      <div style={styles.loadingDots}>
        <motion.div
          style={styles.loadingDot}
          animate={{ scale: [1, 1.3, 1], opacity: [1, 0.5, 1] }}
          transition={{ duration: 1, repeat: Infinity, delay: 0 }}
        />
        <motion.div
          style={styles.loadingDot}
          animate={{ scale: [1, 1.3, 1], opacity: [1, 0.5, 1] }}
          transition={{ duration: 1, repeat: Infinity, delay: 0.2 }}
        />
        <motion.div
          style={styles.loadingDot}
          animate={{ scale: [1, 1.3, 1], opacity: [1, 0.5, 1] }}
          transition={{ duration: 1, repeat: Infinity, delay: 0.4 }}
        />
      </div>
    </div>
  );

  return (
    <div style={styles.container}>
      <AnimatePresence mode="wait">
        {/* Step 1: Select Contact */}
        {currentStepName === 'selectContact' && (
          <motion.div
            key="selectContact"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
          >
            <div style={styles.formGroup}>
              <label style={styles.label}>Select Contact *</label>
              <select style={styles.select}>
                <option>{demoContact.name} - {demoContact.company}</option>
              </select>

              <div style={styles.contactInfo}>
                <div style={styles.contactInfoTitle}>Contact Context:</div>
                <div style={styles.contactInfoText}>
                  <strong>Connection:</strong> {demoContact.connection}<br />
                  <strong>Interests:</strong> {demoContact.interests}
                </div>
              </div>
            </div>
          </motion.div>
        )}

        {/* Step 2: Select Message Type & Medium */}
        {currentStepName === 'selectOptions' && (
          <motion.div
            key="selectOptions"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
          >
            <div style={styles.formGroup}>
              <label style={styles.label}>Message Type</label>
              <select style={styles.select}>
                <option>Follow-up</option>
              </select>
            </div>

            <div style={styles.formGroup}>
              <label style={styles.label}>Communication Medium</label>
              <select style={styles.select}>
                <option>Email</option>
              </select>
              <p style={{ fontSize: '0.85rem', color: '#64748b', marginTop: '0.5rem' }}>
                Professional and structured with subject line
              </p>
            </div>
          </motion.div>
        )}

        {/* Step 3: Select Template */}
        {currentStepName === 'selectTemplate' && (
          <motion.div
            key="selectTemplate"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
          >
            <div style={styles.formGroup}>
              <label style={styles.label}>Message Structure</label>
              <div style={styles.templateCard}>
                <span style={styles.templateLabel}>Warm & Personal</span>
                <div style={styles.templateDescription}>
                  Personal connection → Shared interest → Soft ask
                </div>
              </div>
            </div>
          </motion.div>
        )}

        {/* Step 4: Generating */}
        {currentStepName === 'generating' && (
          <motion.div
            key="generating"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            <LoadingDots />
            <div style={{ textAlign: 'center', color: '#667eea', fontWeight: 600, marginTop: '-1rem' }}>
              <Sparkles size={20} style={{ display: 'inline-block', marginRight: '0.5rem' }} />
              Generating your personalized message...
            </div>
          </motion.div>
        )}

        {/* Step 5: Show Generated Message */}
        {(currentStepName === 'showMessage' || currentStepName === 'userInput') && (
          <motion.div
            key="showMessage"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
          >
            <div style={{ marginBottom: '1rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <label style={styles.label}>Conversation</label>
            </div>

            <div style={styles.conversationContainer}>
              <div style={{ ...styles.messageItem, ...styles.aiMessage }}>
                <div style={styles.messageLabel}>AI GENERATED MESSAGE</div>
                <div style={styles.messageText}>{displayedMessage}</div>
                <div style={styles.actionButtonsContainer}>
                  <button style={{ ...styles.actionButton, ...styles.copyButton }}>
                    <Copy size={14} />
                    Copy
                  </button>
                  <button style={{ ...styles.actionButton, ...styles.saveButton }}>
                    <Save size={14} />
                    Save
                  </button>
                </div>
              </div>
            </div>

            {currentStepName === 'userInput' && (
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
              >
                <label style={styles.label}>Refine with Natural Language</label>
                <p style={{ fontSize: '0.85rem', color: '#64748b', marginBottom: '1rem' }}>
                  Ask for changes like "make it shorter", "add more enthusiasm", or "mention their recent promotion"
                </p>
                <div style={styles.refinementInput}>
                  <textarea
                    value={displayedRefinement}
                    readOnly
                    style={styles.refinementTextarea}
                  />
                  <button style={styles.sendButton}>
                    <Send size={18} />
                    Send
                  </button>
                </div>
              </motion.div>
            )}
          </motion.div>
        )}

        {/* Step 6: Refining */}
        {currentStepName === 'refining' && (
          <motion.div
            key="refining"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            <div style={styles.conversationContainer}>
              <div style={{ ...styles.messageItem, ...styles.aiMessage }}>
                <div style={styles.messageLabel}>AI GENERATED MESSAGE</div>
                <div style={styles.messageText}>{initialMessage}</div>
              </div>
              <div style={{ ...styles.messageItem, ...styles.userMessage }}>
                <div style={styles.messageLabel}>YOU</div>
                <div style={styles.messageText}>{userRefinement}</div>
              </div>
            </div>
            <LoadingDots />
            <div style={{ textAlign: 'center', color: '#667eea', fontWeight: 600, marginTop: '-1rem' }}>
              <Sparkles size={20} style={{ display: 'inline-block', marginRight: '0.5rem' }} />
              Refining your message...
            </div>
          </motion.div>
        )}

        {/* Step 7: Show Refined Message */}
        {currentStepName === 'showRefined' && (
          <motion.div
            key="showRefined"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
          >
            <div style={{ marginBottom: '1rem' }}>
              <label style={styles.label}>Conversation</label>
            </div>

            <div style={styles.conversationContainer}>
              <div style={{ ...styles.messageItem, ...styles.aiMessage }}>
                <div style={styles.messageLabel}>AI GENERATED MESSAGE</div>
                <div style={styles.messageText}>{initialMessage}</div>
              </div>

              <div style={{ ...styles.messageItem, ...styles.userMessage }}>
                <div style={styles.messageLabel}>YOU</div>
                <div style={styles.messageText}>{userRefinement}</div>
              </div>

              <motion.div
                style={{ ...styles.messageItem, ...styles.aiMessage }}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
              >
                <div style={styles.messageLabel}>AI REFINED MESSAGE</div>
                <div style={styles.messageText}>{displayedMessage}</div>
                <div style={styles.actionButtonsContainer}>
                  <button style={{ ...styles.actionButton, ...styles.copyButton }}>
                    <Copy size={14} />
                    Copy
                  </button>
                  <button style={{ ...styles.actionButton, ...styles.saveButton }}>
                    <Save size={14} />
                    Save
                  </button>
                </div>
              </motion.div>
            </div>
          </motion.div>
        )}

        {/* Step 8: Complete */}
        {currentStepName === 'complete' && (
          <motion.div
            key="complete"
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0 }}
            style={{ textAlign: 'center', padding: '3rem' }}
          >
            <motion.div
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ type: 'spring', stiffness: 200 }}
            >
              <Check
                size={64}
                color="#10b981"
                style={{
                  background: '#d1fae5',
                  borderRadius: '50%',
                  padding: '1rem',
                  marginBottom: '1rem',
                }}
              />
            </motion.div>
            <h3 style={{ fontSize: '1.5rem', fontWeight: 700, marginBottom: '0.5rem', color: '#0f172a' }}>
              Perfect Message Created!
            </h3>
            <p style={{ color: '#64748b' }}>
              Ready to send your personalized, AI-crafted message
            </p>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default AnimatedMessageDemo;
